import { z } from '@customerio/hydra-validation';
import { getApiClientErrorBody, getApiClientErrorStatus } from '@/services/api/errors';
import type { SqlDatabaseType, SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import type { SqlImportDatabaseWriteInput } from '@/services/db/sql-import-database/mutations';
import { isEmptyString } from '@/utils/string';
import { transformSqlImportError } from '@/features/sql-sync';

/** The address customers allow through their firewall so the sync can reach a TCP database, per data center. */
export const SQL_SERVER_IPS = {
  us: '34.122.196.49',
  eu: '104.155.37.221',
} as const satisfies Record<'us' | 'eu', string>;

/**
 * Engines addressed by host and port; Snowflake takes an account name and BigQuery a project.
 * Accepts the raw `type` a saved database carries, which the API types as a plain string.
 */
export function usesPort(type: string): boolean {
  return type === 'mysql' || type === 'postgres' || type === 'redshift' || type === 'sqlserver';
}

/** Engines the sync can reach over an SSL connection with customer-supplied certificates. */
export function usesSsl(type: string): boolean {
  return type === 'mysql' || type === 'postgres' || type === 'redshift';
}

/**
 * What the connection form edits. Every engine shares one shape so the form has one schema type;
 * which fields are shown, required, and sent depends on the engine.
 */
export type DatabaseFormValues = {
  name: string;
  host: string;
  /** Kept as typed; parsed to a number on submit. */
  port: string;
  warehouse: string;
  role: string;
  username: string;
  /** The password, or for BigQuery the service-account key file JSON. Never read back from the server. */
  password: string;
  pem_private_key: string;
  ssl: boolean;
  ssl_server_ca: string;
  ssl_client_key: string;
  ssl_client_cert: string;
  project_id: string;
};

export type DatabaseFormField = keyof DatabaseFormValues;

const FORM_FIELDS: readonly DatabaseFormField[] = [
  'name',
  'host',
  'port',
  'warehouse',
  'role',
  'username',
  'password',
  'pem_private_key',
  'ssl',
  'ssl_server_ca',
  'ssl_client_key',
  'ssl_client_cert',
  'project_id',
];

function toFormField(key: string): DatabaseFormField | undefined {
  return FORM_FIELDS.find((field) => field === key);
}

/**
 * The form's starting values: a saved database's non-secret settings, or blanks for a new one. The
 * server never returns a secret, so every secret starts blank and an edit enters it again.
 */
export function databaseFormDefaults(database: SqlImportDatabase | undefined): DatabaseFormValues {
  const port = database?.port ?? 0;
  return {
    name: database?.name ?? '',
    host: database?.host ?? '',
    port: port === 0 ? '' : String(port),
    warehouse: database?.warehouse ?? '',
    role: database?.role ?? '',
    username: database?.username ?? '',
    password: '',
    pem_private_key: '',
    ssl: database?.ssl ?? false,
    ssl_server_ca: '',
    ssl_client_key: '',
    ssl_client_cert: '',
    project_id: database?.project_id ?? '',
  };
}

const BLANK_MESSAGE = "can't be blank";
const PORT_MESSAGE = 'must be a positive number';
const PERIOD_MESSAGE = 'cannot start or end with a period';
const SSL_MESSAGE = 'Provide the CA, client key, and client certificate together, or none of them';

const SSL_FIELDS = ['ssl_server_ca', 'ssl_client_key', 'ssl_client_cert'] as const;

function isPositiveInteger(value: string): boolean {
  return /^\d+$/.test(value.trim()) && Number(value) > 0;
}

/**
 * Whether the SSL certificate fields are all filled or all blank. MySQL rejects a partial set, so
 * the form blocks it; the other SSL engines take any combination.
 */
export function isSslCompliant(values: DatabaseFormValues, type: SqlDatabaseType): boolean {
  if (type !== 'mysql' || !values.ssl) {
    return true;
  }
  const filled = SSL_FIELDS.filter((field) => !isEmptyString(values[field])).length;
  return filled === 0 || filled === SSL_FIELDS.length;
}

function addBlankIssue(ctx: z.RefinementCtx, field: DatabaseFormField): void {
  ctx.addIssue({ code: 'custom', path: [field], message: BLANK_MESSAGE });
}

function refinePort(values: DatabaseFormValues, type: SqlDatabaseType, ctx: z.RefinementCtx) {
  if (!usesPort(type)) {
    return;
  }
  if (isEmptyString(values.port)) {
    addBlankIssue(ctx, 'port');
  } else if (!isPositiveInteger(values.port)) {
    ctx.addIssue({ code: 'custom', path: ['port'], message: PORT_MESSAGE });
  }
}

function refineSnowflake(values: DatabaseFormValues, ctx: z.RefinementCtx) {
  const host = values.host.trim();
  if (!isEmptyString(host) && (host.startsWith('.') || host.endsWith('.'))) {
    ctx.addIssue({ code: 'custom', path: ['host'], message: PERIOD_MESSAGE });
  }
  if (isEmptyString(values.pem_private_key)) {
    addBlankIssue(ctx, 'pem_private_key');
  }
}

function refineSsl(values: DatabaseFormValues, type: SqlDatabaseType, ctx: z.RefinementCtx) {
  if (isSslCompliant(values, type)) {
    return;
  }
  const firstBlank = SSL_FIELDS.find((field) => isEmptyString(values[field])) ?? SSL_FIELDS[0];
  ctx.addIssue({ code: 'custom', path: [firstBlank], message: SSL_MESSAGE });
}

function refineTcp(values: DatabaseFormValues, type: SqlDatabaseType, ctx: z.RefinementCtx) {
  for (const field of ['name', 'username', 'host'] as const) {
    if (isEmptyString(values[field])) {
      addBlankIssue(ctx, field);
    }
  }
  refinePort(values, type, ctx);
  if (type === 'snowflake') {
    refineSnowflake(values, ctx);
  }
  refineSsl(values, type, ctx);
}

/**
 * The engine's field rules, mirroring what the backend requires before it tries the connection.
 * The same rules apply to an edit: the backend never merges stored secrets, so the Snowflake key
 * and the BigQuery key file have to be entered again (Ember let the edit through and the backend
 * rejected it).
 */
export function databaseFormSchema(type: SqlDatabaseType) {
  return z
    .object({
      name: z.string(),
      host: z.string(),
      port: z.string(),
      warehouse: z.string(),
      role: z.string(),
      username: z.string(),
      password: z.string(),
      pem_private_key: z.string(),
      ssl: z.boolean(),
      ssl_server_ca: z.string(),
      ssl_client_key: z.string(),
      ssl_client_cert: z.string(),
      project_id: z.string(),
    })
    .superRefine((values, ctx) => {
      if (type === 'bigquery') {
        for (const field of ['project_id', 'password'] as const) {
          if (isEmptyString(values[field])) {
            addBlankIssue(ctx, field);
          }
        }
        return;
      }
      refineTcp(values, type, ctx);
    });
}

/**
 * The write body for the engine. Secrets go as typed, blank included: the backend verifies the
 * connection with exactly what it receives and never merges the stored settings, so an edit has
 * to carry its password again. Snowflake has no port and BigQuery only a project and key file.
 */
export function databaseWriteInput(type: SqlDatabaseType, values: DatabaseFormValues): SqlImportDatabaseWriteInput {
  if (type === 'bigquery') {
    return { type, project_id: values.project_id.trim(), password: values.password };
  }
  const base: SqlImportDatabaseWriteInput = {
    type,
    name: values.name.trim(),
    host: values.host.trim(),
    username: values.username.trim(),
  };
  if (usesPort(type)) {
    base.port = Number(values.port);
  }
  if (type === 'snowflake') {
    return {
      ...base,
      warehouse: values.warehouse.trim(),
      role: values.role.trim(),
      pem_private_key: values.pem_private_key,
    };
  }
  const withPassword = { ...base, password: values.password };
  if (!usesSsl(type)) {
    return withPassword;
  }
  return {
    ...withPassword,
    ssl: values.ssl,
    ssl_server_ca: values.ssl ? values.ssl_server_ca : '',
    ssl_client_key: values.ssl ? values.ssl_client_key : '',
    ssl_client_cert: values.ssl ? values.ssl_client_cert : '',
  };
}

/** The card title for a saved database: its name, or the project for BigQuery. */
export function databaseTitle(database: SqlImportDatabase): string {
  if (!isEmptyString(database.name)) {
    return database.name;
  }
  if (!isEmptyString(database.project_id)) {
    return database.project_id;
  }
  return 'no name';
}

/** The card description: where the database is reached, or null when nothing is saved yet. */
export function databaseAddress(database: SqlImportDatabase): string | null {
  const parts = [database.host, database.port === 0 ? '' : String(database.port ?? '')].filter(
    (part) => !isEmptyString(part),
  );
  return parts.length === 0 ? null : parts.join(' ');
}

export type DatabaseSaveFailure =
  /** The settings were well-formed but the backend could not connect with them. */
  | { kind: 'connection'; detail: string }
  /** One or more fields were rejected; each maps onto a form field. */
  | { kind: 'fields'; errors: { field: DatabaseFormField; message: string }[] }
  /** Anything else: not a validation response. */
  | { kind: 'other' };

const ATTRIBUTE_POINTER = '/data/attributes/';

/** One validation entry as the backend writes it: a detail and the attribute it points at. */
const wireErrorSchema = z.object({ detail: z.string(), source: z.object({ pointer: z.string() }) });

type WireError = { detail: string; pointer: string };

function toWireError(entry: unknown): WireError | null {
  const parsed = wireErrorSchema.safeParse(entry);
  return parsed.success ? { detail: parsed.data.detail, pointer: parsed.data.source.pointer } : null;
}

function wireErrors(error: unknown): WireError[] {
  const body = getApiClientErrorBody(error);
  if (body == null || typeof body !== 'object' || !('errors' in body) || !Array.isArray(body.errors)) {
    return [];
  }
  return body.errors.map(toWireError).filter((entry) => entry != null);
}

/**
 * Sort a failed save into what the form can do about it. The backend reports validation as 422
 * with one entry per attribute; the `database` attribute is the connection attempt itself.
 */
export function parseDatabaseSaveError(error: unknown): DatabaseSaveFailure {
  if (getApiClientErrorStatus(error) !== 422) {
    return { kind: 'other' };
  }
  const entries = wireErrors(error);
  const connection = entries.find((entry) => entry.pointer === `${ATTRIBUTE_POINTER}database`);
  if (connection != null) {
    return { kind: 'connection', detail: connection.detail };
  }
  const errors: { field: DatabaseFormField; message: string }[] = [];
  for (const entry of entries) {
    if (!entry.pointer.startsWith(ATTRIBUTE_POINTER)) {
      continue;
    }
    const field = toFormField(entry.pointer.slice(ATTRIBUTE_POINTER.length));
    if (field != null) {
      errors.push({ field, message: entry.detail });
    }
  }
  return errors.length === 0 ? { kind: 'other' } : { kind: 'fields', errors };
}

export { transformSqlImportError };
